import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import AccessGate from '../components/AccessGate';
import LeafletMap from '../components/LeafletMap';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

export default function Contact() {
  const { org, submitContact } = useAuth();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const orgName = (org && org.name) || 'Royal Australian Engineers’ Association of Tasmania';

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim() || !form.message.trim()) {
      setErr('Please provide your name and a message.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setErr('Please provide a valid email address.');
      return;
    }
    setBusy(true);
    try {
      await submitContact(form);
      setSent(true);
    } catch (e2) {
      setErr((e2.response && e2.response.data && e2.response.data.detail) || 'Sorry, your message could not be sent. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AccessGate pageKey="contact">
      <div className="max-w-4xl mx-auto px-4 py-10 fade-in">
        <h1 className="font-title text-3xl md:text-4xl mb-2" style={{ color: 'var(--maroon)' }}>Contact Us</h1>
        <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <p className="text-[15px] mb-5">
              The Royal Australian Engineer’s Association of Tasmania welcomes enquiries from Sappers,
              family members and the public. Use the form or the details below to get in touch.
            </p>
            <ul className="space-y-3 text-[15px]">
              <li className="flex items-center gap-3"><Mail size={18} style={{ color: 'var(--navy)' }} /> secretary@tassappers.com.au</li>
              <li className="flex items-center gap-3"><MapPin size={18} style={{ color: 'var(--navy)' }} /> Hobart, Tasmania, Australia</li>
            </ul>
            <div className="mt-5">
              <LeafletMap
                markers={[{ lat: -42.8821, lng: 147.3272, label: `${orgName} — Hobart, Tasmania` }]}
                center={[-42.8821, 147.3272]}
                zoom={12}
                height={260}
                singlePin
                scrollWheelZoom
                testid="contact-map"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            {sent ? (
              <div className="text-center py-6" data-testid="contact-success">
                <CheckCircle2 size={44} className="mx-auto mb-3" style={{ color: 'var(--navy)' }} />
                <h3 className="font-title text-xl mb-1">Message Sent</h3>
                <p className="text-sm" style={{ color: 'var(--olive)' }}>Thank you — we will be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="space-y-4">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} data-testid="contact-honeypot" />
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" data-testid="contact-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" data-testid="contact-message-input" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="contact-submit-button"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded font-title transition-colors hover:opacity-90"
                  style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}
                >
                  <Send size={16} /> {busy ? 'Sending…' : 'Send Message'}
                </button>
                {err && <p className="text-sm mt-1" style={{ color: '#8a1f11' }} data-testid="contact-error">{err}</p>}
              </form>
            )}
          </div>
        </div>
      </div>
    </AccessGate>
  );
}
